/**
 * MAP TEST Navigator (내부 이름: MAP TEST 노트 · URL /note) 학생 노출 문구 — 유일 정의 지점.
 * 2026-10-11 대표 확정: 학생용 코너 이름 "MAP TEST 노트" → "MAP TEST Navigator" · "매일 업데이트" → "Updated Daily". 관리자 탭 이름은 그대로.
 *
 * NOTE_COPY 의 이벤트·홈 문구는 2026-10-10 대표 시안 확정본이다
 * (`docs/plan/maptest_note.md` §6-1). 대표 승인 없이 바꾸지 않는다.
 * NOTE_UI 는 기능 동작용 짧은 라벨(탭·검색·페이지 이동)이다.
 * 노트 화면은 이 파일 밖에서 문구를 하드코딩하지 않는다.
 */
import type { NoteCategory, NoteSubject, NoteTab } from "./types";

export const NOTE_COPY = {
  heroTitleLine1: "MAP TEST 점수를",
  heroTitleLine2: "확실하게 성장시킵니다",
  heroCta: "MAP TEST 모의고사 무료 체험하기", // 2026-10-26 화살표 대신 흰 동그라미 Start (page.tsx Hero)
  participantsLabel: "이번 달 참여 인원",
  participantsUnit: "명",
  /** 예전 활동 카드 (ActivityStatsCard) 표기 그대로 — 라벨 옆 실시간 배지. */
  participantsLive: "LIVE",
  /** 메타 description 등 한 문자열이 필요한 곳. 화면 제목 줄은 boardTitleMain + boardTitleSub 를 따로 쓴다. */
  boardTitle: "MAP TEST Navigator · Updated Daily",
  /** 제목 줄: 모바일은 왼쪽 제목 · 오른쪽 보조, 데스크톱은 한 줄에 제목 + 작은 회색 보조 (2026-10-11 대표 확정). */
  boardTitleMain: "MAP TEST Navigator",
  boardTitleSub: "Updated Daily",
  /** 제목 줄 바로 아래 한 줄 (홈 게시판 · /note · 2026-10-11 대표 확정). */
  boardIntro: "MAP TEST에 대한 모든 정보, 여기서 확인하세요.",
  /** 메타 title (/note · 상세 접미사). */
  listMetaTitle: "MAP TEST Navigator | MAP Test Experts",
  /** 탭 페이지 메타 title — 과목(탭) 이름 포함 (2026-10-12 #47). */
  listTabMetaTitle: (tab: string) => `${tab} · MAP TEST Navigator | MAP Test Experts`,
  detailMetaSuffix: "MAP TEST Navigator",
  /** 글 목록 첫 줄 고정 공지 행의 빨간 라벨 (2026-10-11 대표 확정). 문구·"자세히 →" 는 noteTopBannerLead · noteTopBannerMore. */
  noticeLabel: "공지",
  viewAll: "MAP TEST Navigator 전체 보기 →",
  noteTopBanner: "좋아요·방문하고 MAP TEST 모의고사 응시권 받기 · 자세히 →",
  /** noteTopBanner 를 두 조각으로 렌더 (뒤 조각이 규칙 안내 열기 버튼). 합치면 noteTopBanner 와 같다. */
  noteTopBannerLead: "좋아요·방문하고 MAP TEST 모의고사 응시권 받기",
  noteTopBannerMore: "자세히 →",
  postFooter: "이 글에 좋아요하고 응시권 받으세요",
  welcomeTitle: "가입을 환영합니다",
  welcomeBody: "좋아요·방문만 해도 모의고사 응시권을 드립니다",
  welcomeCta: "MAP TEST Navigator 보러 가기 →",
} as const;

/**
 * 이벤트 안내창 v2 (2026-10-11 대표 명세 · 컴포넌트 src/components/note/NoteEventModal.tsx).
 * 숫자는 src/lib/note/config.ts 상수에서만 온다 (하드코딩 금지).
 * 2026-10-11: 공식 NWEA 응시권 상 카드·공유 단계 제거 (보상은 모의고사 응시권만).
 * 예전 번호 목록 규칙 7줄 (NOTE_EVENT_RULES) 은 이 안내창으로 대체됐다.
 */
export const NOTE_EVENT_MODAL = {
  tag: "MAP TEST Navigator EVENT",
  titleLine1: "글 읽고 좋아요만 눌러도",
  titleLine2: "MAP TEST 모의고사 응시권을 드립니다",
  subtitle: "로그인하면 자동으로 쌓이고, 자동으로 지급됩니다",
  repeatHeading: "매번 받을 수 있는 응시권",
  likeCardTitle: (n: number) => `좋아요 ${n}개`,
  streakCardTitle: (n: number) => `${n}일 연속 방문`,
  cardVoucher: "MAP TEST 모의고사 응시권 1장",
  likeCardPill: (n: number) => `${n}개마다 반복`,
  streakCardPill: (n: number) => `${n}일마다 반복`,
  stepsHeading: "이렇게 모으세요",
  steps: (visitSeconds: number) =>
    [
      { title: "마음에 드는 글에 좋아요", note: "글 하나에 한 번" },
      { title: `매일 들러서 ${visitSeconds}초 이상 둘러보기`, note: "하루 한 번 방문으로 기록" },
    ] as const,
  notice: (cap: number) =>
    `좋아요는 하루(한국 시간) ${cap}개까지 인정됩니다. 응시권은 관리자 승인 없이 자동 지급되며, 마이페이지에서 바로 확인할 수 있습니다.`,
  myStatus: "내 현황 보기",
  goList: "글 보러 가기 →",
  close: "닫기",
} as const;

/** 본문 예시 문제 카드 (:::quiz) — 2026-10-11 대표 요청 ("정답 보기" 버튼 · 정답·오답 표시 · 해설). */
export const NOTE_QUIZ = {
  showAnswer: "정답 보기",
  correct: "정답",
  wrong: "오답",
  answerIs: (k: string) => `정답 ${k}`,
  why: "해설",
} as const;

/** 상세 하단 버튼 (♥ 좋아요 · ↗ 공유하기). */
export const NOTE_ACTION = {
  like: "좋아요",
  share: "공유하기",
} as const;

/**
 * 글 하단 모의고사 CTA 박스 (2026-10-12 #47 · #47-2 시안 A · NoteCta.tsx) — 소제목 · 2줄 제목 · 버튼 3줄만 (보조문구·부제 금지).
 * 제목은 [첫 줄, 둘째 줄] · `**…**` 은 빨간 강조 (홈 섹션 제목 강조와 같은 색).
 * subject 가 없거나 reading·math·language 가 아니면 (모의고사 활용·기타) other.
 */
export const NOTE_CTA = {
  eyebrow: "Try It Yourself",
  title: {
    reading: ["오늘 본 독해 포인트,", "**실제 시험 화면**에서 풀어보세요"],
    math: ["오늘 본 개념,", "**실제 MAP TEST 화면**에서 확인해보세요"],
    language: ["오늘 본 문법 포인트,", "**실제 시험 화면**에서 풀어보세요"],
    other: ["읽기만 하면 남지 않아요.", "**직접 풀어봐야** 내 수준이 보여요"],
  },
  button: "MAP TEST 모의고사 무료 체험하기",
} as const;

/** 글 subject → 제목 2줄 (강조 표시 `**…**` 포함). */
export function noteCtaTitle(subject: string | null | undefined): readonly [string, string] {
  return subject === "reading" || subject === "math" || subject === "language" ? NOTE_CTA.title[subject] : NOTE_CTA.title.other;
}

/** 한 줄을 강조/일반 조각으로 나눈다 (`**…**` → em=true). */
export function splitEmphasis(line: string): { text: string; em: boolean }[] {
  return line
    .split(/(\*\*[^*]+\*\*)/)
    .filter(Boolean)
    .map((t) => (t.startsWith("**") && t.endsWith("**") ? { text: t.slice(2, -2), em: true } : { text: t, em: false }));
}

/** 마이페이지 "MAP TEST Navigator 이벤트 현황" — 대표 시안 라벨 (이름만 2026-10-11 변경). */
export const NOTE_STATUS = {
  title: "MAP TEST Navigator 이벤트 현황",
  likes: "누른 좋아요",
  likesToday: (n: number) => `오늘 ${n}개`,
  streak: "연속 방문",
  streakLongest: (n: number) => `최장 ${n}일`,
  remaining: "오늘 남은 좋아요",
  remainingSub: (cap: number) => `하루 ${cap}개까지`,
  sessionsCard: "받은 모의고사 응시권",
  sessionsCardSub: "좋아요·방문만 해도 무료로 받아요", // 2026-10-20 대표 "좋아요·방문 보상 대신 다른 멘트"
  sessionsTitle: "MAP TEST 모의고사 응시권",
  sessionsTotal: (n: number) => `지금까지 받은 응시권 ${n}장`,
  barLike: (a: number, b: number) => `좋아요 20개마다 1장 · ${a} / ${b}`,
  barStreak: (a: number) => `10일 연속 방문마다 1장 · ${a} / 10일`,
  historyTitle: "최근 지급·인정 내역",
  /**
   * 공식 NWEA 무료권 — 신규 지급 중단 (2026-10-11) · 이미 지급된 계정에만 예전 제목 그대로 표시 (대표 확정: 유지).
   * 가격은 src/lib/pricing.ts::MAP_TEST_PRICE_1SUBJECT 를 넘겨 받는다.
   */
  voucherTitle: (price: number) => `공식 NWEA MAP TEST 1과목 응시권 (${price.toLocaleString("ko-KR")}원 상당)`,
  voucherUnused: "미사용",
  voucherUsed: "사용 완료",
} as const;

/** 공식 MAP 신청 화면 무료권 버튼. */
export const NOTE_FREE_REGISTER = "무료권으로 1과목 신청";

/** 목록 탭 라벨 — "전체 / MAP Reading / MAP Language / MAP Math / 모의고사 활용" (2026-10-11 대표 확정 · 순서 = types.ts NOTE_TABS). */
export const NOTE_ALL_LABEL = "전체";
export const NOTE_TAB_LABEL: Record<NoteTab, string> = {
  reading: "MAP Reading",
  language: "MAP Language",
  math: "MAP Math",
  mock_usage: "모의고사 활용",
};

/**
 * "국제학교별 입학전형" 탭·화면 문구 (2026-10-11 블록 A/2 · B/2 공개 — verified 항목 있는 승인 학교가 있을 때만 탭 표시).
 * 공식 도메인 근거 없는 값은 "공지 없음" · 비인가는 "학력 미인정" · TPI 언급 0.
 */
export const NOTE_SCHOOL = {
  tabLabel: "국제학교별 입학전형",
  title: "국제학교별 입학전형",
  intro: "학교 공식 홈페이지에 공지된 내용만 정리합니다. 공지가 없으면 \"공지 없음\"으로 표시합니다.",
  kind: { intl_accredited: "인가 국제학교", foreign_school: "외국인학교", unaccredited: "비인가" } as Record<string, string>,
  unaccreditedBadge: "학력 미인정",
  recruit: { open: "접수 중", upcoming: "예정", closed: "마감", none: "공지 없음" } as Record<string, string>,
  noNotice: "공지 없음",
  applyEnd: "마감일",
  exams: "입학시험",
  domestic: "내국인 지원 요건",
  lastChecked: "마지막 확인",
  homepage: "학교 홈페이지",
  filterAll: "전체",
  filterKind: "구분",
  filterRegion: "지역",
  empty: "표시할 학교가 없습니다.",
} as const;

/** 글 라벨용 카테고리 이름 (목록 행·상세 상단·관리자). 탭 이름은 NOTE_TAB_LABEL. */
export const NOTE_CATEGORY_LABEL: Record<NoteCategory, string> = {
  map_info: "MAP 정보",
  mock_usage: "모의고사 활용",
  story: "MAP TEST 이야기",
};

/** "MAP 정보" 글의 과목별 라벨 (2026-10-11 대표 확정). 홈 판 · /note 목록 · 상세 상단. */
export const NOTE_SUBJECT_INFO_LABEL: Record<NoteSubject, string> = {
  reading: "MAP Reading 정보",
  math: "MAP Math 정보",
  language: "MAP Language Usage 정보",
  common: "MAP 공통 정보",
};

/** 글 라벨 — "MAP 정보" 는 과목 라벨 (과목 없으면 "MAP 정보"), 다른 카테고리는 그대로. */
export function noteLabel(category: NoteCategory, subject: NoteSubject | null | undefined): string {
  if (category === "map_info" && subject) return NOTE_SUBJECT_INFO_LABEL[subject];
  return NOTE_CATEGORY_LABEL[category];
}

/**
 * 홈 Navigator 목록 전용 짧은 카테고리명 (2026-10-13 #50 대표 요청) — 홈 판 (NoteRow variant="home") 에서만 쓴다.
 * /note 탭 · /note 목록 · 글 상세 · OG 는 기존 이름 (noteLabel · NOTE_TAB_LABEL) 그대로.
 * 대표 지정 4개 (Reading · Math · Language · 모의고사) + 지정 없는 나머지는 같은 길이 이하로 (공통 · 정보 · 이야기).
 */
export const NOTE_HOME_SHORT_SUBJECT_LABEL: Record<NoteSubject, string> = {
  reading: "Reading",
  math: "Math",
  language: "Language",
  common: "공통",
};
export const NOTE_HOME_SHORT_CATEGORY_LABEL: Record<NoteCategory, string> = {
  map_info: "정보",
  mock_usage: "모의고사",
  story: "이야기",
};

/** 홈 목록 라벨 — "MAP 정보" 는 과목 짧은 이름, 다른 카테고리는 짧은 이름. */
export function noteHomeLabel(category: NoteCategory, subject: NoteSubject | null | undefined): string {
  if (category === "map_info" && subject) return NOTE_HOME_SHORT_SUBJECT_LABEL[subject];
  return NOTE_HOME_SHORT_CATEGORY_LABEL[category];
}

/** 글 주제 RIT 범위 (2026-10-27 대표 지시 · 제목 앞 · rit.ts::noteRitFor). */
export function noteRitLabel(rit: { low: number; high: number }): string {
  return `RIT ${rit.low}–${rit.high}`;
}

export const NOTE_UI = {
  views: "조회",
  sortLatest: "최신순",
  searchPlaceholder: "검색",
  searchLabel: "MAP TEST Navigator 검색",
  backToList: "MAP TEST Navigator 목록으로",
  /** 상세 이전/다음 글 (2026-10-11 대표 요청) — 위 한 줄 "‹ 이전 글 · 목록 · 다음 글 ›" · 아래 카드 2개. */
  prevPost: "‹ 이전 글",
  nextPost: "다음 글 ›",
  list: "목록",
  prevPage: "이전",
  nextPage: "다음",
  empty: "글이 없습니다.",
  prevImage: "이전 이미지",
  nextImage: "다음 이미지",
  close: "닫기",
  sources: "출처",
} as const;

/** 질문하면 관련 글 찾기 (2026-10-16 대표 요청 · 홈 게시판·/note 목록 공지 바로 위 · NoteAskBox.tsx). */
export const NOTE_ASK = {
  label: "MAP TEST Navigator에 질문하기",
  placeholder: "MAP TEST에 대하여 궁금한 모든 것을 찾아보세요.",
  button: "찾기",
  searching: "찾는 중…",
  resultsTitle: (n: number) => `관련 글 ${n}개`,
  empty: "딱 맞는 글을 아직 찾지 못했어요. 남겨 주신 질문은 다음 글 주제로 참고하겠습니다.",
  tooShort: "두 글자 이상 적어 주세요.",
  error: "잠시 후 다시 시도해 주세요.",
  close: "닫기",
} as const;

/** 사이트 안에서는 숨기는 본문 마지막 줄 (외부 배포용 꼬리표). */
export const NOTE_EXTERNAL_TAIL = "👉 maptest.kr";
